import { Schema, type } from "@colyseus/schema";

/**
 * One active buff instance on a cube (synced via Colyseus). The buff *kind* is
 * looked up via `BUFF_DEFS[buffId]` for stat-mod data; the instance only
 * carries timing + the attacker attribution (`sourceId`) for DoT-style buffs.
 *
 * Why a Schema (not a plain object): client HUD will read these to render
 * buff icons + duration arcs in a future tier. Keeping the shape synced now
 * means that tier is a pure-render addition with no protocol change.
 */
export class BuffInstance extends Schema {
  /** Matches `BUFF_DEFS` registry key. Empty string is the schema default; live instances always have a real id. */
  @type("string") buffId: string = "";

  /** Server ms when the buff expires (`Date.now() + durationMs` at apply). */
  @type("number") expiresAt: number = 0;

  /** Server ms when the buff was first applied. Useful for "duration so far" UI. */
  @type("number") appliedAt: number = 0;

  /** Server ms when onTick last fired. Cadence-aware tick uses this to skip until next due. */
  @type("number") lastTickAt: number = 0;

  /**
   * SessionId of the cube that applied this buff. Used by DoT-style behaviors
   * to attribute damage on each tick (kill credit, hit broadcast, postDamage
   * subscribers). Empty string for sourceless buffs (Frost Armor — self-cast,
   * no attacker). Tier 2 customer: AfflictionBuff.
   */
  @type("string") sourceId: string = "";

  /**
   * Pre-HP damage absorption pool. Decremented by the buff shield combat hook
   * each time the carrier takes damage; the buff expires early when this hits
   * zero (`expiresAt` clamps forward). Stays at 0 for non-shield buffs (the
   * shield hook only reads buffs whose `BUFF_BEHAVIORS[id].shield === true`).
   *
   * Tier 3 customers: StanceShieldBuff (initial = 3.5 × sacrificed HP),
   * PowerWordShieldBuff (initial = base + (WIL−18) × perWillpower).
   */
  @type("uint16") remainingShield: number = 0;

  /**
   * Remaining "next-N attacks" charges for arming-rider buffs (Backstab,
   * SiphonLife). Decremented by `fireSwingResolveRiders` on each swing the
   * carrier delivers; the buff is removed when this hits 0 (charge-depletion
   * path mirrors natural-expiry — onExpire fires before removeBuff).
   *
   * Stays at 0 for non-rider buffs (the dispatcher only reads buffs whose
   * `BUFF_BEHAVIORS[id].onSwingResolve` is defined). uint8 cap = 255 is
   * enormously past any plausible spell value (KTS max is 5 charges).
   *
   * Tier 8 customers: BackstabBuff (initial = 3), SiphonLifeBuff (initial = 3).
   */
  @type("uint8") chargesLeft: number = 0;
}
